import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TutorStructureNode } from '../../../services/tutorStructureService';
import {
  getLifecycle,
  setPlannedDate,
  clearPlannedDate,
  markStarted,
  markCompleted,
  TopicLifecycleDTO,
} from '../../../services/tutorLifecycleService';
import {
  isFlaggedForRevision,
  flagForRevision,
  unflagForRevision,
} from '../../../services/tutorRevisionService';

interface NodeDetailDrawerProps {
  node: TutorStructureNode | null;
  courseId: number;
  onClose: () => void;
  onLabelSave: (nodeId: number, label: string) => Promise<void>;
  onDescriptionSave: (nodeId: number, description: string) => Promise<void>;
  onDelete: (node: TutorStructureNode) => void;
}

export default function NodeDetailDrawer({
  node,
  courseId,
  onClose,
  onLabelSave,
  onDescriptionSave,
  onDelete,
}: NodeDetailDrawerProps) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [lifecycle, setLifecycle] = useState<TopicLifecycleDTO | null>(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [plannedDateInput, setPlannedDateInput] = useState('');
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);

  const [revisionFlagged, setRevisionFlagged] = useState<boolean | null>(null);
  const [revisionLoading, setRevisionLoading] = useState(false);

  useEffect(() => {
    if (node) {
      setLabel(node.label);
      setDescription(node.description ?? '');
      setLifecycle(null);
      setRevisionFlagged(null);
      setLifecycleError(null);
      setPlannedDateInput('');

      if (node.nodeType === 'TOPIC_REF' || node.nodeType === 'CUSTOM_TOPIC') {
        setLifecycleLoading(true);
        getLifecycle(courseId, node.id)
          .then(lc => {
            setLifecycle(lc);
            setPlannedDateInput(lc.plannedDate ?? '');
          })
          .finally(() => setLifecycleLoading(false));

        setRevisionLoading(true);
        isFlaggedForRevision(courseId, node.id)
          .then(setRevisionFlagged)
          .finally(() => setRevisionLoading(false));
      }
    }
  }, [node?.id]);

  if (!node) return null;

  const isTopic = node.nodeType === 'TOPIC_REF' || node.nodeType === 'CUSTOM_TOPIC';

  const handleLabelBlur = async () => {
    if (label.trim() && label !== node.label) {
      await onLabelSave(node.id, label.trim());
    }
  };

  const handleDescriptionBlur = async () => {
    if (description !== (node.description ?? '')) {
      await onDescriptionSave(node.id, description);
    }
  };

  const handleSetPlannedDate = async () => {
    if (!plannedDateInput) return;
    setLifecycleError(null);
    try {
      const lc = await setPlannedDate(courseId, node.id, plannedDateInput);
      setLifecycle(lc);
    } catch (e: any) {
      setLifecycleError(e?.response?.data?.message ?? 'Failed to set planned date');
    }
  };

  const handleClearPlannedDate = async () => {
    setLifecycleError(null);
    try {
      const lc = await clearPlannedDate(courseId, node.id);
      setLifecycle(lc);
      setPlannedDateInput('');
    } catch (e: any) {
      setLifecycleError(e?.response?.data?.message ?? 'Failed to clear planned date');
    }
  };

  const handleMarkStarted = async () => {
    setLifecycleError(null);
    try {
      const lc = await markStarted(courseId, node.id);
      setLifecycle(lc);
    } catch (e: any) {
      setLifecycleError(e?.response?.data?.message ?? 'Failed to mark started');
    }
  };

  const handleMarkCompleted = async () => {
    setLifecycleError(null);
    try {
      const lc = await markCompleted(courseId, node.id);
      setLifecycle(lc);
    } catch (e: any) {
      setLifecycleError(e?.response?.data?.message ?? 'Failed to mark completed');
    }
  };

  const handleToggleRevision = async () => {
    setRevisionLoading(true);
    try {
      if (revisionFlagged) {
        await unflagForRevision(courseId, node.id);
        setRevisionFlagged(false);
      } else {
        await flagForRevision(courseId, node.id);
        setRevisionFlagged(true);
      }
    } finally {
      setRevisionLoading(false);
    }
  };

  const nodeTypeLabel =
    node.nodeType === 'WRAPPER' ? 'Section / Wrapper' :
    node.nodeType === 'TOPIC_REF' ? 'Library Topic' : 'Custom Topic';

  const lifecycleStateColor = (state: string) => {
    if (state === 'COMPLETED') return 'success';
    if (state === 'STARTED') return 'primary';
    if (state === 'SCHEDULED') return 'info';
    return 'default';
  };

  return (
    <>
      <Drawer anchor="right" open={!!node} onClose={onClose} PaperProps={{ sx: { width: 340, p: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Chip label={nodeTypeLabel} size="small" color={node.isLibraryLinked ? 'primary' : 'default'} />
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="caption" color="text.secondary" gutterBottom>
          Label
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={handleLabelBlur}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          sx={{ mb: 2 }}
        />

        <Typography variant="caption" color="text.secondary" gutterBottom>
          Description (optional)
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          placeholder="Add notes for yourself or context for students…"
          sx={{ mb: 3 }}
        />

        {isTopic && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Teaching Lifecycle
            </Typography>
            {lifecycleLoading ? (
              <CircularProgress size={18} sx={{ my: 1 }} />
            ) : lifecycle ? (
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={lifecycle.state}
                    size="small"
                    color={lifecycleStateColor(lifecycle.state) as any}
                  />
                </Box>

                {lifecycle.state === 'NONE' || lifecycle.state === 'SCHEDULED' ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      type="date"
                      size="small"
                      value={plannedDateInput}
                      onChange={(e) => setPlannedDateInput(e.target.value)}
                      disabled={lifecycle.state === 'STARTED' || lifecycle.state === 'COMPLETED'}
                      sx={{ flex: 1 }}
                      InputLabelProps={{ shrink: true }}
                      label="Planned date"
                    />
                    <Button size="small" variant="outlined" onClick={handleSetPlannedDate}
                      disabled={!plannedDateInput}>
                      Set
                    </Button>
                    {lifecycle.state === 'SCHEDULED' && (
                      <Button size="small" color="inherit" onClick={handleClearPlannedDate}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                ) : null}

                {lifecycle.state !== 'COMPLETED' && (
                  <Stack direction="row" spacing={1}>
                    {lifecycle.state !== 'STARTED' && lifecycle.state !== 'COMPLETED' && (
                      <Button size="small" variant="contained" onClick={handleMarkStarted}>
                        Mark Started
                      </Button>
                    )}
                    {lifecycle.state === 'STARTED' && (
                      <Button size="small" variant="contained" color="success" onClick={handleMarkCompleted}>
                        Mark Completed
                      </Button>
                    )}
                  </Stack>
                )}

                {lifecycleError && (
                  <Alert severity="error" sx={{ py: 0 }}>{lifecycleError}</Alert>
                )}
              </Stack>
            ) : null}

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Revision
            </Typography>
            {revisionLoading ? (
              <CircularProgress size={18} />
            ) : (
              <Button
                size="small"
                variant={revisionFlagged ? 'contained' : 'outlined'}
                color={revisionFlagged ? 'warning' : 'inherit'}
                onClick={handleToggleRevision}
              >
                {revisionFlagged ? 'Remove Revision Flag' : 'Flag for Revision'}
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {revisionFlagged ? 'Students see a "Revision" badge on this topic.' : 'Flag this topic to alert students to revisit it.'}
            </Typography>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Button
          variant="outlined"
          color="error"
          fullWidth
          onClick={() => setConfirmOpen(true)}
        >
          Delete node
        </Button>
      </Drawer>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete node?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {node.children && node.children.length > 0
              ? `This ${nodeTypeLabel.toLowerCase()} contains ${node.children.length} child topic(s) that will also be deleted. This cannot be undone.`
              : 'This node will be permanently deleted.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmOpen(false);
              onClose();
              onDelete(node);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
